// Uproszczony service do pobierania danych z GitHub
class DataService {
  constructor() {
    // Twarde kodowanie konfiguracji GitHub
    this.githubConfig = {
      username: 'TEHAQUE',
      repo: 'website-API',
      branch: 'main',
      filePath: 'db.js'
    };
    this.token = process.env.REACT_APP_GITHUB_TOKEN;
    // Prosty cache in-memory + deduplikacja wywołań
    this._cache = null;
    this._cacheTime = 0;
    this._pending = null;
    this.cacheTTL = 60 * 60 * 1000; // 1 godzina zamiast 10 minut
    
    console.log('🔧 DataService Config:', {
      username: this.githubConfig.username,
      repo: this.githubConfig.repo,
      branch: this.githubConfig.branch,
      hasToken: !!this.token
    });
  }

  async fetchData() {
    console.log('🔄 Pobieranie danych z GitHub...');
    // Zwróć z cache jeśli świeży
    const now = Date.now();
    if (this._cache && (now - this._cacheTime) < this.cacheTTL) {
      console.log('🗂️ Zwracam dane z cache (jedno żądanie na sesję)');
      return this._cache;
    }
    // Jeśli zapytanie już trwa – zwróć tę samą obietnicę
    if (this._pending) {
      console.log('⏳ Dołączam do trwającego pobierania danych (deduplikacja)');
      return this._pending;
    }

    try {
      this._pending = (async () => {
        const { username, repo, branch, filePath } = this.githubConfig;
        const apiUrl = `https://api.github.com/repos/${username}/${repo}/contents/${filePath}?ref=${branch}`;
        
        console.log('🔍 URL API:', apiUrl);

        const headers = {
          'Accept': 'application/vnd.github.v3+json'
        };
        if (this.token) {
          headers['Authorization'] = `token ${this.token}`;
        }
        
        const response = await fetch(apiUrl, {
          headers
        });
        
        console.log('📡 Status odpowiedzi:', response.status);
        
        if (!response.ok) {
          throw new Error(`Błąd API: ${response.status}`);
        }
        
        const fileInfo = await response.json();
        console.log('📄 Informacje o pliku:', {
          name: fileInfo.name,
          size: fileInfo.size,
          sha: fileInfo.sha?.substring(0, 8)
        });
        
        // Dekoduj zawartość z base64 z obsługą UTF-8
        const base64Content = fileInfo.content.replace(/\n/g, '');
        
        // Użyj TextDecoder dla poprawnego dekodowania UTF-8
        const binaryString = atob(base64Content);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const jsCode = new TextDecoder('utf-8').decode(bytes);
        
        console.log('📝 Zawartość pliku (pierwsze 200 znaków):', jsCode.substring(0, 200));
        
        // Parsuj kod JavaScript
        const cleanCode = jsCode
          .replace(/export\s+const\s+/g, 'const ')
          .replace(/export\s+/g, '');
        
        // Dodaj kod do wyciągnięcia danych
        const extractCode = cleanCode + `
          // Zwróć wszystkie dane
          ({
            authors: typeof authors !== 'undefined' ? authors : [],
            news: typeof news !== 'undefined' ? news : [],
            updates: typeof updates !== 'undefined' ? updates : [],
            projects: typeof projects !== 'undefined' ? projects : [],
            categories: typeof categories !== 'undefined' ? categories : [],
            achievements: typeof achievements !== 'undefined' ? achievements : {},
            announcements: typeof announcements !== 'undefined' ? announcements : [],
            WWD: typeof WWD !== 'undefined' ? WWD : [],
            AboutNews: typeof AboutNews !== 'undefined' ? AboutNews : [],
            glossary: typeof glossary !== 'undefined' ? glossary : {},
            roadmap: typeof roadmap !== 'undefined' ? roadmap : []
          });
        `;
        
        // Wykonaj kod i pobierz dane
        // eslint-disable-next-line no-eval
        const data = eval(extractCode);
        
        console.log('✅ Dane pobrane z GitHub:', {
          authors: data.authors?.length || 0,
          news: data.news?.length || 0,
          updates: data.updates?.length || 0,
          projects: data.projects?.length || 0,
          categories: data.categories?.length || 0,
          announcements: data.announcements?.length || 0,
          WWD: data.WWD?.length || 0,
          AboutNews: data.AboutNews?.length || 0,
          glossary: (data.glossary && typeof data.glossary === 'object') ? Object.keys(data.glossary).length : 0,
          roadmap: data.roadmap?.length || 0
        });
        
        // Zapisz do cache i zwróć
        this._cache = data;
        this._cacheTime = Date.now();
        return data;
      })();
      const result = await this._pending;
      this._pending = null;
      return result;
      
    } catch (error) {
      console.error('❌ Błąd pobierania danych z GitHub:', error);
      this._pending = null;
      throw error;
    }
  }

  // Metody pomocnicze
  async getNews() {
    const data = await this.fetchData();
    return data.news || [];
  }

  async getProjects() {
    const data = await this.fetchData();
    return data.projects || [];
  }

  async getAuthors() {
    console.log('👥 Pobieranie autorów z GitHub...');
    const data = await this.fetchData();
    const authors = data.authors || [];
    
    console.log('👥 Znaleziono autorów:', authors.length);
    console.log('👥 Lista autorów:', authors.map(author => ({
      id: author.id,
      nickname: author.nickname,
      bio: author.bio ? author.bio.substring(0, 50) + '...' : 'Brak bio'
    })));
    
    return authors;
  }

  async getCategories() {
    const data = await this.fetchData();
    return data.categories || [];
  }

  async getUpdates() {
    const data = await this.fetchData();
    const updates = data.updates || data.news || [];
    console.log('📅 Znaleziono aktualizacje:', updates.length);
    return updates;
  }

  async getAchievements() {
    const data = await this.fetchData();
    return data.achievements || {};
  }

  async getAnnouncements() {
    const data = await this.fetchData();
    return data.announcements || [];
  }

  async getWWD() {
    const data = await this.fetchData();
    return data.WWD || [];
  }

  async getProjectUpdates(projectId) {
    const updates = await this.getUpdates();
    return updates.find(update => update.projectId === projectId) || updates[0] || null;
  }

  getImageUrl(imagePath) {
    return `/images/${imagePath}`;
  }

  // Wyczyść cache (używane przez DataContext.refresh)
  clearCache() {
    this._cache = null;
    this._cacheTime = 0;
    this._pending = null;
    console.log('🧹 Cache dataService wyczyszczony');
  }
}

// Eksportuj instancję
export const dataService = new DataService();

// Eksportuj funkcje pomocnicze
export const getNews = () => dataService.getNews();
export const getProjects = () => dataService.getProjects();
export const getAuthors = () => dataService.getAuthors();
export const getCategories = () => dataService.getCategories();
export const getUpdates = () => dataService.getUpdates();
export const getAchievements = () => dataService.getAchievements();
export const getAnnouncements = () => dataService.getAnnouncements();
export const getWWD = () => dataService.getWWD();
export const getProjectUpdates = (projectId) => dataService.getProjectUpdates(projectId);
